/**
 * Shared display formatters for cost / token / latency / duration. The
 * Cost Dashboard, the Job Detail summary block, and the per-call trace
 * panel all consume the same shapes, so the helpers live here rather
 * than being duplicated per route.
 *
 * Keep the contract narrow: one function per display concern, no
 * conditional branches in arguments. Routes compose them.
 */

/**
 * `formatJobNumber` lives in `@prospect/shared` so the SPA, the
 * orchestrator's URL builders, and the email templates all share one
 * source of truth. Re-exported here so SPA imports stay pinned to
 * `@/lib/format` for consistency with the other display formatters.
 */
export { formatJobNumber } from "@prospect/shared";

/**
 * USD with 4-decimal precision. Matches the dashboard's headline
 * `$0.0019` framing — the underlying `cost_usd` column carries 6
 * decimals; truncating at 4 gives a readable summary while still
 * surfacing fractions of a cent for cheap calls.
 */
export const usd = (v: string | number): string => `$${Number(v).toFixed(4)}`;

/** Thousands-separated integer (`1,234`). */
export const compact = (n: number): string =>
  new Intl.NumberFormat("en-US").format(n);

/** Latency in milliseconds, thousands-separated, suffixed `ms`. */
export const ms = (n: number): string => `${compact(Math.round(n))}ms`;

/**
 * Wall-clock duration formatted with adaptive units:
 * - `< 1s` → `123ms`
 * - `< 1m` → `12.3s`
 * - otherwise → `12m 34s`
 *
 * Returns the placeholder `"—"` when the input is null — used for
 * in-flight rows where one of the two timestamps hasn't landed yet.
 */
export function formatDurationMs(value: number | null): string {
  if (value === null) return "—";
  if (value < 1000) return `${value}ms`;
  if (value < 60_000) return `${(value / 1000).toFixed(1)}s`;
  const minutes = Math.floor(value / 60_000);
  const seconds = Math.round((value % 60_000) / 1000);
  return `${minutes}m ${seconds}s`;
}

/**
 * Compute a duration in milliseconds from two ISO timestamps, returning
 * null when either side is missing or unparseable. The Cost Dashboard's
 * runs table feeds in `(startedAt, completedAt)` strings; the Job
 * Detail summary feeds in the same shape.
 */
export function diffMsFromIso(
  startIso: string | null,
  endIso: string | null,
): number | null {
  if (!startIso || !endIso) return null;
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.max(0, end - start);
}
